import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePodAssignments } from 'infrastructure/hooks/ccm/use-pod-assignments';
import PodDetailTooltip from 'components/molecules/rpm-overview-table/components/pod-metadata/pod-tooltip';
import PodMetadataLine from 'components/molecules/rpm-overview-table/components/pod-metadata';
import { POD_SECTION_CONFIG } from 'components/molecules/rpm-overview-table/constants';

import type { FC } from 'react';
import type { IPodAssignmentsResponse } from 'utils/hooks/rpmOverviewDashboard/usePodAssignments';

// ─── PodLabel Component ───────────────────────────────────────────────────────

interface IPodLabelProps {
  podSection: 'primary' | 'secondary' | 'tertiary' | 'outside';
  podName: string | null;
  podGuid: string | null;
  patientClinicGuid?: string | null;
}

export const PodLabel: FC<IPodLabelProps> = ({
  podSection,
  podName,
  podGuid,
  patientClinicGuid,
}) => {
  const { t } = useTranslation();
  const { podMap, isLoading, isError } = usePodAssignments('CCM');

  // Resolve clinic data from cache for tooltip (O(1) lookup via Map)
  const podData = podGuid ? podMap.get(podGuid) : undefined;

  // Use "Unknown Pod" fallback so the tooltip is not suppressed when podName is absent
  const resolvedPodName =
    podName ?? (podData ? t('labels.pods.unknownPod', 'Unknown Pod') : null);

  // Adapt CCM pod data to the shape expected by PodDetailTooltip
  const syntheticAssignments = useMemo<
    IPodAssignmentsResponse | undefined
  >(() => {
    if (!podData || !resolvedPodName) return undefined;
    return {
      pods: [
        {
          guid: podData.guid,
          podName: resolvedPodName,
          podType: podData.podType,
          priority: podSection,
          clinics: (podData.clinics ?? []).map((c) => ({
            guid: c.guid,
            name: c.name,
            state: c.state,
          })),
        },
      ],
    };
  }, [podData, podSection, podName, resolvedPodName]);

  return (
    <PodDetailTooltip
      podSection={podSection}
      podName={resolvedPodName}
      podAssignments={syntheticAssignments}
      podAssignmentsError={isError}
      podAssignmentsLoading={isLoading}
      patientClinicGuid={patientClinicGuid}
      priorityColor={POD_SECTION_CONFIG[podSection].textColor}
    >
      <PodMetadataLine
        podSection={podSection}
        podName={podName}
        podInfoTextColor={POD_SECTION_CONFIG[podSection].textColor}
      />
    </PodDetailTooltip>
  );
};

export default PodLabel;
